/* Global imports. */
import React from "react"
import { graphql, Link } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

/* Class imports. */
import Breadcrumb from "@classes/Breadcrumb";

/* Component imports. */
import BookSlider from "@components/BookSlider"
import Breadcrumbs from "@components/Breadcrumbs";
import Layout from "@components/Layout"
import RelatedArticles from "@src/components/RelatedArticles";
import SEO from "@components/SEO";
import ShareOnTwitter from "@components/ShareOnTwitter";
import ShareOnFacebook from "@components/ShareOnFacebook";

/* Other imports. */
import { cleanStringForPath } from "@src/helpers";

/* Model imports. */
import { PanMacmillanContributor, PanMacmillanEdition } from "@src/models/panmacmillan"

const Template = ({ data }: any) => {
  const contributor: PanMacmillanContributor = data.panMacmillanContributor;
  const editions: [PanMacmillanEdition] = data.panMacmillanContributor.primaryEditions
  const editionsWithUniqueCollection:any = []
  let relatedArticles = contributor.relatedArticles && contributor.relatedArticles.length > 0 ? contributor.relatedArticles : []
  const image = contributor.remoteImage && getImage(contributor.remoteImage)
  const imageDescription = `Contributor image for ${contributor && contributor.personName}`

  relatedArticles.sort(
    (a:KontentArticle, b:KontentArticle) => {
      const datePartsA = a.elements.display_date.value.split('/')
      const dateA = new Date(+datePartsA[2], datePartsA[1] - 1, +datePartsA[0]).setUTCHours(0, 0, 0, 0)
      const datePartsB = b.elements.display_date.value.split('/')
      const dateB = new Date(+datePartsB[2], datePartsB[1] - 1, +datePartsB[0]).setUTCHours(0, 0, 0, 0)
        
      return dateA - dateB
    }
  ).reverse()

  relatedArticles = relatedArticles.length > 10 ? relatedArticles.slice(0,10) : relatedArticles;

  editions && editions.forEach(x => {
    return x.collection && x.collection.length > 0 && x.collection.forEach(collection => {
      if(collection.collectionIdentifier && collection.collectionIdentifier !== '' &&  collection.collectionIdentifier !== "PanMac28535") {
        const index = editionsWithUniqueCollection.findIndex((e) => e.collectionIdentifier === collection.collectionIdentifier);
        if(index === -1) {
          editionsWithUniqueCollection.push(collection)
        }
      }
    })
  })

  const breadcrumbs = [
    new Breadcrumb("Home", "/"),
    new Breadcrumb("Authors & Illustrators", "/authors"),
    new Breadcrumb(contributor.personName, `/${contributor.slug}`)
  ];

  return (
    <Layout>
      <main id="contributor" className="container-main">
        <div className="">
          <div className="mb-2 md:mb-6">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <section className="grid justify-between grid-cols-12 sm:gap-x-8">
          <aside className="grid self-start w-auto grid-cols-12 col-span-full sm:col-span-4 sm:gap-x-4 gap-y-12">
              <figure className="col-span-6 col-start-4 sm:col-span-12 sm:col-start-1">
              {image ? 
              <GatsbyImage image={image} alt={imageDescription} className="rounded-md" />
              :
              <img src={contributor.sourceImagePath} alt={imageDescription} className="rounded-md" width="350" height="531" />
              }
              </figure>
            </aside>
            <section className="grid self-start w-auto max-w-full grid-cols-1 gap-8 sm:col-start-5 col-span-full sm:col-span-8">
              <section className="relative">
                <h1 className="mt-8 mb-4 text-3xl sm:mt-0 sm:mb-8 sm:text-6xl" dangerouslySetInnerHTML={{ __html: contributor.personName }} />
                <div className="font-serif biography" dangerouslySetInnerHTML={{ __html: contributor.biography }} />
              </section>
              
              {editions && 
              <section className="relative">
                <h2 className="mb-2 font-sans text-xl font-bold sm:mb-4 sm:text-3xl section--heading">Books by <span dangerouslySetInnerHTML={{ __html: contributor.personName }} /></h2>
                <BookSlider editions={editions} referringSlug={`/${contributor.slug}`} />
              </section>
              }

              {editionsWithUniqueCollection.length > 0 && 
              <section className="series-list-wrap js-series-section series-list-wrap--show">
                <p className="mb-2 font-sans text-xl font-bold sm:mb-4 sm:text-3xl">Series by <span dangerouslySetInnerHTML={{ __html: contributor.personName }} /></p>
                <ul className="js-series-wrap !pl-0 !list-none">
                  {editionsWithUniqueCollection.map((edition:PanMacmillanEdition) => <li className="py-2 text-lg leading-7">
                        <Link className="no-underline text-red" to={`/series/${cleanStringForPath(edition.collectionTitle)}/${cleanStringForPath(edition.collectionIdentifier)}`}>
                        {edition.collectionTitle}
                        </Link>
                      </li>
                  )}
                </ul>
              </section>
              }

            {relatedArticles.length > 0 &&
              <section className="related-book--wrap">
                <RelatedArticles
                    title="Related articles"
                    format="landscape"
                    articleSummaries={relatedArticles}
                    sectionClass="col-span-12"
                    listClass="flex flex-col gap-5 !pl-0 !list-none"
                    headingClass="mb-2 font-sans text-xl sm:mb-4 sm:text-3xl font-bold"
                    componentID="related-articles-contributor"
                  />
                
              </section>
              }
              
            </section>
          </section>
        </div>
      </main>
    </Layout>
  );
};

export default Template;

export const Head = ({data}) => {
  const contributor: PanMacmillanContributor = data.panMacmillanContributor;

  return (
    <SEO 
      title={contributor.personName} 
      description={contributor.personName}
      url={contributor.slug}
      contentType="contributor"
      image = {contributor.sourceImagePath}
      />
  )
}

/* GraphQL schema. */
export const query = graphql`
  query panMacmillanContributorQuery($id: String!) {
    panMacmillanContributor(id: { eq: $id }) {
      id
      slug
      contributorID
      personName
      biography
      primaryEditions {
        isbn
        title
        titleFull
        primaryContributorName
        format
        fields {
          slug
        }
        isPrimaryEdition
        collection {
          collectionIdentifier
          collectionTitle
        }
        sourceImagePath
        remoteImage {
          publicURL
        }
        dimensions {
          width
          height
        }
      }
      sourceImagePath
      remoteImage {
        publicURL
      }
      relatedArticles {
        fields {
          slug
        }
        elements {
          title {
            value
          }
          url_slug {
            value
          }
          display_date {
            value(formatString: "DD/MM/YYYY")
          }
          article_categories {
              value {
                  name
              }
          }
          image {
              value {
                  width
                  url
                  type
                  size
                  name
                  height
                  description
              }
          }
        }
      }
      additionalContributorDetails {
        elements {
          twitter_id {
            value
          }
          contributor_id {
            value
          }
          facebook_id {
            value
          }
          newsletter_forms__newsletter_form {
            value {
              ... on kontent_item_newsletter_form {
                id
                system {
                  codename
                }
              }
            }
          }
        }
      }
    }
  }
`